import {
  IFreshOptionsResponse,
  FreshMealPlanType,
  GTMProduct,
} from "libs/api/customer/src/lib/types/checkout";
import { ICapiCustomData } from "@capi/types";

import { ISignupFlowStore } from "./types";

export interface IFreshPricingExtras {
  product: string;
  quantity: number;
  trial: { price: number }
  ongoing: { price: number }
}

export interface IFreshPricingDelivery {
  ongoing: number;
  trial: number;
  date: string;
}

export interface IFreshPricingPerCatPricing {
  id: string;
  name: string;
  ongoing: {
    pricePerDay: number;
  };
  trial: {
    pricePerDay: number;
  };
}

export interface IFreshPricingDisplay {
  trial: {
    originalTotal: number;
    discountedTotal: number;
    originalPerCatPerDay: number;
    discountedPerCatPerDay: number;
  };
  ongoing: {
    originalTotal: number;
    discountedTotal: number | undefined;
    originalPerCatPerDay: number;
    discountedPerCatPerDay: number | undefined;
  };
  delivery: IFreshPricingDelivery
  nibblesUpsell?: IFreshPricingExtras;
  sprinklesUpsell?: IFreshPricingExtras;
  discount?: {
    code: string;
  };
  perCatPricing: IFreshPricingPerCatPricing[]
}
export const mapFreshOptionsResponseToPricingDisplay = (
  freshOptions: IFreshOptionsResponse,
  cats: number,
): IFreshPricingDisplay => {
  if (cats <= 0) {
    const emptySubscription = {
      originalTotal: 0,
      discountedTotal: 0,
      originalPerCatPerDay: 0,
      discountedPerCatPerDay: 0,
    };
    return {
      trial: emptySubscription,
      ongoing: emptySubscription,
      nibblesUpsell: undefined,
      sprinklesUpsell: undefined,
      discount: undefined,
      delivery: {
        ongoing: 0,
        trial: 0,
        date: "",
      },
      perCatPricing: [],
    };
  }

  const {
    totalSubscription,
    totalSubscriptionWithDiscount,
    totalTrialDiscount,
    totalTrialNoDiscount,
    discount,
    deliveryDate,
  } = freshOptions;
  const { code } = discount ?? {};

  // Trial
  const discountedPerCatPerDayTrial = (totalTrialDiscount?.freshMealPlanPricePerDay ?? 0) / cats;
  const originalPerCatPerDayTrial = (totalTrialNoDiscount?.freshMealPlanPricePerDay ?? 0) / cats;

  // Ongoing
  const discountedPerCatPerDayOngoing = totalSubscriptionWithDiscount
    ? (totalSubscriptionWithDiscount.freshMealPlanPricePerDay / cats)
    : undefined;
  const originalPerCatPerDayOngoing = (totalSubscription?.freshMealPlanPricePerDay ?? 0) / cats;

  // WE NEED TO TEST THIS THROUGHLY
  // THE INFO WE ARE GETTING FROM THE API IS CONFUSING
  const extras = Object.values(totalTrialDiscount?.extrasPerTitle ?? {}).map((extra) => ({
    product: extra.productTitle,
    quantity: extra.quantity,
    ongoing: {
      price: extra.pricePerItem,
    },
    trial: {
      price: extra.price,
    },
  }));

  const nibblesUpsell = extras.find((extra) => extra.product === "NIBBLES_CHICKEN" || extra.product === "NIBBLES_SALMON");
  const sprinklesUpsell = extras.find((extra) => extra.product === "SPRINKLES");

  const perCatPricing = Object.values(freshOptions.catIdToData).map((cat) => ({
    id: cat.catId,
    name: cat.catName,
    ongoing: {
      pricePerDay: cat.trialNoDiscount.freshMealPlanPricePerDay,
    },
    trial: {
      pricePerDay: cat.trialDiscount?.freshMealPlanPricePerDay ?? 0,
    },
  }));

  return {
    trial: {
      originalTotal: totalTrialNoDiscount?.totalPrice,
      discountedTotal: totalTrialDiscount?.totalPrice,
      originalPerCatPerDay: originalPerCatPerDayTrial,
      discountedPerCatPerDay: discountedPerCatPerDayTrial,
    },
    ongoing: {
      originalTotal: totalSubscription?.totalPrice,
      discountedTotal: totalSubscriptionWithDiscount?.totalPrice,
      originalPerCatPerDay: originalPerCatPerDayOngoing,
      discountedPerCatPerDay: discountedPerCatPerDayOngoing,
    },
    delivery: {
      ongoing: totalSubscription?.deliveryPrice,
      trial: totalTrialDiscount?.deliveryPrice ?? 0,
      date: deliveryDate,
    },
    nibblesUpsell,
    sprinklesUpsell,
    perCatPricing,
    discount: code ? {
      code,
    } : undefined,
  };
};

export const transformCats = (cats: ISignupFlowStore["cats"], perCatPricing: IFreshPricingPerCatPricing[]) => {
  const updatedCats: ISignupFlowStore["cats"] = { ...cats };
  for (const catId of Object.keys(updatedCats)) {
    const cat = updatedCats[catId];
    const catPricing = perCatPricing.find((pc) => pc.name.toLowerCase() === cat.name?.toLowerCase());

    updatedCats[catId] = {
      ...cat,
      trialPricePerDay: catPricing?.trial.pricePerDay,
      ongoingPricePerDay: catPricing?.ongoing.pricePerDay,
    };
  }
  return updatedCats;
};

/**
 * Helper function to categorise a GTM product
 */
const categoriseGTMProduct = (gtmProduct: GTMProduct): string => {
  if (gtmProduct.name.includes("Delivery")) {
    return "Delivery Fee";
  }
  if (gtmProduct.name === "Sprinkles") {
    return "extra";
  }
  if (gtmProduct.id.includes("mealplan")) {
    return "meal_plan";
  }
  return "recipe";
};

/**
 * Creates CAPI custom data from Fresh Options Response for tracking purposes.
 * Uses the gtmProducts array which contains all items (recipes, extras, delivery, meal plan).
 */
export const createCapiCustomDataFromFreshOptions = (
  response: IFreshOptionsResponse,
  product: FreshMealPlanType = "FRESH_TRIAL",
): ICapiCustomData => {
  const { totalTrialDiscount, discount, gtmProducts } = response;

  // Map gtmProducts directly to CAPI contents format
  const contents = gtmProducts?.map((gtmProduct: GTMProduct) => ({
    id: gtmProduct.id,
    quantity: gtmProduct.quantity,
    itemPrice: Number.parseFloat(gtmProduct.price) || 0,
    title: gtmProduct.name,
    brand: gtmProduct.brand,
    deliveryCategory: "home_delivery" as const,
    category: categoriseGTMProduct(gtmProduct),
  })) || [];

  return {
    value: totalTrialDiscount.totalPrice,
    currency: "gbp",
    contentName: product,
    contentCategory: "fresh",
    contents,
    contentType: "product",
    numItems: Object.keys(response.catIdToData).length,
    searchString: discount?.code,
    deliveryCategory: "home_delivery",
  };
};
